import { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronRight, ChevronLeft, X } from 'lucide-react';

export interface WalkthroughStep {
  target: string;
  title: string;
  description: string;
}

interface WalkthroughProps {
  steps: WalkthroughStep[];
  active: boolean;
  onComplete: () => void;
  onBeforeStep?: (stepIndex: number, target: string) => void;
}

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

type TooltipPlacement = 'below' | 'above';

const PADDING = 8;
const TOOLTIP_GAP = 32;

function getSpotlightRect(el: Element): SpotlightRect {
  const r = el.getBoundingClientRect();
  return {
    top: r.top - PADDING + window.scrollY,
    left: r.left - PADDING + window.scrollX,
    width: r.width + PADDING * 2,
    height: r.height + PADDING * 2,
  };
}

export function Walkthrough({ steps, active, onComplete, onBeforeStep }: WalkthroughProps) {
  const [current, setCurrent] = useState(0);
  const [rect, setRect] = useState<SpotlightRect | null>(null);
  const [placement, setPlacement] = useState<TooltipPlacement>('below');
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Reset step to 0 when activated
  useEffect(() => {
    if (active) setCurrent(0);
  }, [active]);

  // Escape key dismisses
  useEffect(() => {
    if (!active) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onComplete();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [active, onComplete]);

  // Measure + position on step change
  const measure = useCallback(() => {
    const step = steps[current];
    if (!step) return;

    const el = document.querySelector(`[data-tour="${step.target}"]`);
    if (!el) return;

    // Scroll into view if needed
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    // Small delay after scroll to get accurate rect
    requestAnimationFrame(() => {
      const sr = getSpotlightRect(el);
      setRect(sr);

      // Decide placement: if target is in upper half, place below; otherwise above
      const viewportMiddle = window.innerHeight / 2;
      const elCenter = el.getBoundingClientRect().top + el.getBoundingClientRect().height / 2;
      setPlacement(elCenter < viewportMiddle ? 'below' : 'above');
    });
  }, [current, steps]);

  useEffect(() => {
    if (!active) return;

    // Fire onBeforeStep callback (e.g., to expand accordion)
    onBeforeStep?.(current, steps[current]?.target ?? '');

    // Delay measure to allow accordion to expand
    const timer = setTimeout(measure, 150);
    return () => clearTimeout(timer);
  }, [current, active, measure, onBeforeStep, steps]);

  // Recalculate on resize/scroll
  useEffect(() => {
    if (!active) return;
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [active, measure]);

  if (!active || !rect) return null;

  const step = steps[current]!;
  const isLast = current === steps.length - 1;
  const isFirst = current === 0;

  // Calculate tooltip position (viewport-relative for fixed positioning)
  const spotlightViewportTop = rect.top - window.scrollY;
  const tooltipHeight = 180; // approximate tooltip height
  const NAV_HEIGHT = 60; // navbar height — tooltip must not overlap it

  let computedTop: number | undefined;
  let computedBottom: number | undefined;

  if (placement === 'below') {
    const belowPos = spotlightViewportTop + rect.height + TOOLTIP_GAP;
    if (belowPos + tooltipHeight > window.innerHeight - 16) {
      // Would go off bottom — clamp to bottom of viewport
      computedBottom = 16;
    } else {
      computedTop = Math.max(belowPos, NAV_HEIGHT + 8);
    }
  } else {
    const aboveBottom = window.innerHeight - spotlightViewportTop + TOOLTIP_GAP;
    if (aboveBottom + tooltipHeight > window.innerHeight - NAV_HEIGHT) {
      // Would go off top / overlap navbar — place just below navbar
      computedTop = NAV_HEIGHT + 8;
    } else {
      computedBottom = aboveBottom;
    }
  }

  // Clamp horizontally
  const tooltipWidth = 320;
  let tooltipLeft = rect.left + rect.width / 2 - tooltipWidth / 2;
  if (tooltipLeft < 16) tooltipLeft = 16;
  if (tooltipLeft + tooltipWidth > window.innerWidth - 16) {
    tooltipLeft = window.innerWidth - tooltipWidth - 16;
  }

  return (
    <div className="fixed inset-0 z-[9999]" aria-modal="true">
      {/* Backdrop with spotlight cutout via box-shadow + orange highlight */}
      <div
        className="absolute inset-0 pointer-events-auto"
        onClick={onComplete}
        style={{
          boxShadow: `
            0 0 0 4000px rgba(0, 0, 0, 0.6),
            0 0 0 2px rgba(249, 115, 22, 0.8),
            0 0 20px 6px rgba(249, 115, 22, 0.25)
          `,
          position: 'fixed',
          top: spotlightViewportTop,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          borderRadius: 8,
          pointerEvents: 'none',
        }}
      />

      {/* Clickable overlay regions around the spotlight */}
      <div
        className="fixed inset-0"
        style={{ clipPath: `polygon(
          0 0, 100% 0, 100% 100%, 0 100%,
          0 ${spotlightViewportTop}px,
          ${rect.left}px ${spotlightViewportTop}px,
          ${rect.left}px ${spotlightViewportTop + rect.height}px,
          ${rect.left + rect.width}px ${spotlightViewportTop + rect.height}px,
          ${rect.left + rect.width}px ${spotlightViewportTop}px,
          0 ${spotlightViewportTop}px
        )` }}
        onClick={onComplete}
      />

      {/* Tooltip card */}
      <div
        ref={tooltipRef}
        className="fixed rounded-xl border border-glass-border shadow-2xl p-4 pointer-events-auto bg-bg-2"
        style={{
          width: tooltipWidth,
          left: tooltipLeft,
          ...(computedTop !== undefined ? { top: computedTop } : {}),
          ...(computedBottom !== undefined ? { bottom: computedBottom } : {}),
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Step counter + skip */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-bold text-ink-4">
            {current + 1} of {steps.length}
          </span>
          <button
            type="button"
            onClick={onComplete}
            className="flex items-center gap-1 text-[10px] font-semibold text-ink-4 hover:text-ink cursor-pointer transition-colors"
          >
            Skip
            <X size={10} />
          </button>
        </div>

        {/* Title + description */}
        <h3 className="text-sm font-extrabold text-ink mb-1">{step.title}</h3>
        <p className="text-[11px] text-ink-3 leading-relaxed mb-3">{step.description}</p>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setCurrent((c) => c - 1)}
            disabled={isFirst}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-[10px] font-bold transition-colors cursor-pointer ${
              isFirst
                ? 'text-ink-4/40 cursor-not-allowed'
                : 'text-ink-3 hover:text-ink hover:bg-surface'
            }`}
          >
            <ChevronLeft size={12} />
            Back
          </button>

          <button
            type="button"
            onClick={() => {
              if (isLast) {
                onComplete();
              } else {
                setCurrent((c) => c + 1);
              }
            }}
            className="flex items-center gap-1 px-4 py-1.5 rounded-lg bg-orange text-white text-[10px] font-bold hover:bg-orange/90 cursor-pointer transition-colors"
          >
            {isLast ? 'Got it' : 'Next'}
            {!isLast && <ChevronRight size={12} />}
          </button>
        </div>
      </div>
    </div>
  );
}
